import { useField, useFormikContext } from 'formik';
import {
  getCountryCallingCode,
  CountryCode,
  Metadata,
} from 'libphonenumber-js/max';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import Skeleton from 'react-loading-skeleton';
import { FontSizes } from '../typography/typography.styles';
import {
  FieldContainer,
  InputContainer,
  LabelTooltipContainer,
} from '../form-field-text/form-field-text.styles';
import { FieldPhoneContainer } from './form-field-phone.styles';
import PhoneCodeDropdown, {
  CountryOption,
} from './phone-code-dropdown/phone-code-dropdown.component';
import { MarginProps } from '../base-margin/base-margin.component';
import {
  TValidationMethod,
  getIsFieldInvalid,
} from '../../utils/forms/forms.utils';
import FieldLabel from '../input/field-label.component';
import InputMessage from '../input-message/input-message.component';
import HintTooltip from '../hint-tooltip/hint-tooltip.component';
import { useIsMobile } from '../../hooks/use-is-mobile.hook';
import { InfoTextContainer } from '../input/input.styles';
import { CountryDetails } from '../../models/shipmentCountryInfo';
import { DEFAULT_COUNTRY } from '../../stores/localizationsStore';

interface Props {
  allowedPhoneCountries: CountryDetails[];
  phoneFieldName: string;
  codeFieldName: string;
  placeholder: string;
  label?: string;
  labelColor?: string;
  isDropdownDisabled?: boolean;
  isInputDisabled?: boolean;
  validationMethod?: TValidationMethod;
  defaultCountryCode?: string;
  tooltipText?: string;
  readOnly?: boolean;
  triggerValidation?: boolean;
  hideSvgOnDisabled?: boolean;
}

export const getCountryCallingCodeWrapper = (countryCode: string) =>
  getCountryCallingCode(countryCode.toLocaleUpperCase() as CountryCode);

const metadata = new Metadata();

const FormFieldPhone = ({
  allowedPhoneCountries,
  phoneFieldName,
  codeFieldName,
  placeholder,
  label,
  labelColor,
  $mb,
  isDropdownDisabled = false,
  isInputDisabled = false,
  validationMethod = 'default',
  defaultCountryCode,
  tooltipText,
  readOnly = false,
  triggerValidation = false,
  hideSvgOnDisabled = true,
}: Props & MarginProps) => {
  const [countryPhoneDetails, setCountryPhoneDetails] = useState<
    CountryOption[]
  >([]);

  const [isFocused, setIsFocused] = useState(false);
  const [maxPhoneLength, setMaxPhoneLength] = useState<number>();
  const [minPhoneLength, setMinPhoneLength] = useState<number>();

  const [field, meta] = useField(phoneFieldName); // phone without country calling code part
  const [codeField] = useField(codeFieldName); // country code (e.g. US, AL)
  const { setFieldValue, validateField } = useFormikContext();
  const isMobile = useIsMobile();

  const { t } = useTranslation();

  useEffect(() => {
    if (allowedPhoneCountries.length === 0) return;

    const phoneDetails: CountryOption[] = allowedPhoneCountries.map((x) => ({
      countryCode: x.code,
      countryName: x.localized_name,
      unicodeFlag: x.emoji,
      callingCode: x.dial_code,
    }));

    setCountryPhoneDetails(phoneDetails);
  }, [allowedPhoneCountries]);

  useEffect(() => {
    if (defaultCountryCode && !codeField.value) {
      setFieldValue(codeFieldName, defaultCountryCode);
    }
  }, [defaultCountryCode, codeField.value, codeFieldName, setFieldValue]);

  // Rare case when provided country code is not the list of allowed countries.
  // In such case - cleanup form

  useEffect(() => {
    if (!codeField.value || allowedPhoneCountries.length === 0) return;

    // If country code is found in the list of allowed countries then all good. Skip cleanup.
    if (allowedPhoneCountries.find((x) => x.code === codeField.value)) return;

    setFieldValue(
      codeFieldName,
      defaultCountryCode ?? DEFAULT_COUNTRY.country_code
    );
    setFieldValue(phoneFieldName, '');
  }, [
    codeField.value,
    allowedPhoneCountries,
    codeFieldName,
    phoneFieldName,
    defaultCountryCode,
    setFieldValue,
  ]);

  const onCountryCodeSelected = (country: CountryOption) => {
    setFieldValue(codeFieldName, country.countryCode);
  };
  const [isTyped, setIsTyped] = useState(false);
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    const validationToUse = triggerValidation ? 'always' : validationMethod;
    const isInvalidPhoneNumber = meta.error === 'invalid_phone_number';
    const isFieldValid =
      !isInvalidPhoneNumber &&
      !getIsFieldInvalid({
        validationMethod: validationToUse,
        meta,
        isTyped,
      });
    setIsValid(isFieldValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, isFocused, isTyped]);

  useEffect(() => {
    if (!triggerValidation) return;

    validateField(field.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerValidation]);

  useEffect(() => {
    if (!codeField.value) return;

    metadata.selectNumberingPlan(codeField.value);
    if (!metadata.numberingPlan) return;
    const possibleLengths = metadata.numberingPlan.possibleLengths();
    const maxLength = Math.max(...possibleLengths); // Length of the part without country phone code
    const minLength = Math.min(...possibleLengths);
    setMaxPhoneLength(maxLength);
    setMinPhoneLength(minLength);
  }, [codeField.value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(field.name, event.target.value.replace(/[^0-9]+/g, ''));
    setIsTyped(true);
  };

  const showCountrySkeleton = countryPhoneDetails.length === 0;
  const shouldShowInvalidPhoneError =
    !isValid && meta.error === 'invalid_phone_number';
  const shouldShowError =
    !isValid && (!isFocused || shouldShowInvalidPhoneError);
  const shouldShowLengthCounter =
    isFocused && !!maxPhoneLength && !shouldShowInvalidPhoneError;

  return (
    <FieldContainer $mb={$mb ?? '2.4rem'}>
      <InputContainer
        $fontSize={FontSizes.normal}
        $padding='1rem 1.2rem'
        $error={!isValid}
        $disabled={isInputDisabled}
        $readOnly={readOnly}>
        <LabelTooltipContainer>
          <FieldLabel
            inputId={phoneFieldName}
            isOptional={!label}
            labelColor={labelColor}
            optionalText='Optional'>
            {label}
          </FieldLabel>
          {tooltipText && (
            <HintTooltip
              contentMaxWidth={isMobile ? '20rem' : '30rem'}
              placement='top'
              text={tooltipText}
            />
          )}
        </LabelTooltipContainer>

        <FieldPhoneContainer>
          {showCountrySkeleton && (
            <Skeleton
              width='87px'
              height='40px'
              style={{ borderRadius: '10px' }}
            />
          )}
          {!showCountrySkeleton && (
            <PhoneCodeDropdown
              hideSvgOnDisabled={hideSvgOnDisabled}
              selectedCountryCode={codeField.value}
              onSelected={onCountryCodeSelected}
              items={countryPhoneDetails}
              isDropdownDisabled={isDropdownDisabled}
            />
          )}
          <input
            type='text'
            id={phoneFieldName}
            placeholder={placeholder}
            required
            inputMode='tel'
            {...field}
            formNoValidate
            autoComplete='false'
            onFocus={() => setIsFocused(true)}
            onBlur={(e) => {
              setIsFocused(false);
              field.onBlur(e);
            }}
            maxLength={maxPhoneLength}
            onChange={handleChange}
            disabled={isInputDisabled}
            readOnly={readOnly}
            data-type={!isValid ? 'invalid-input' : undefined}
          />
        </FieldPhoneContainer>
      </InputContainer>

      {shouldShowError && (
        <InputMessage type='error'>{t(meta.error || '')}</InputMessage>
      )}
      {shouldShowLengthCounter && (
        <InfoTextContainer>
          {getMinMaxLengthText(field.value, minPhoneLength, maxPhoneLength)}
        </InfoTextContainer>
      )}
    </FieldContainer>
  );
};

function getMinMaxLengthText(fieldValue: any, min?: number, max?: number) {
  const leftPart = `${fieldValue?.length ?? 0}`;
  if (!min || min === max) {
    return `${leftPart} / ${max}`;
  }
  return `${leftPart} / ${min} - ${max}`;
}

export default observer(FormFieldPhone);
